import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DollarSign,
  Briefcase,
  Star,
  Loader2,
  CheckCircle,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useMarketplaceStore, type Professional } from '../../stores/marketplace-store';
import { notify } from '../../lib/toast';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalFooter,
} from '../ui/responsive-modal';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface HireModalProps {
  professional: Professional | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function HireModal({ professional, open, onOpenChange, onSuccess }: HireModalProps) {
  const { t } = useTranslation();
  const { createHire, myJobs } = useMarketplaceStore();
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [customRate, setCustomRate] = useState<string>(
    professional?.hourlyRate ? String(professional.hourlyRate / 100) : ''
  );
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!professional) return null;

  const stars = professional.rating / 100;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const agreedRate = customRate ? Math.round(parseFloat(customRate) * 100) : undefined;
      await createHire(professional.id, selectedJob || undefined, agreedRate);
      notify.success(t('hire.offerSent', 'Proposta enviada com sucesso!'));
      // cerimônia: proposta de contratação enviada com sucesso
      window.dispatchEvent(
        new CustomEvent('reformu:moment', { detail: { type: 'hired', name: professional.user.name } })
      );
      onSuccess?.();
      onOpenChange(false);
    } catch (error: any) {
      notify.error(error.message || t('hire.offerFailed', 'Falha ao enviar proposta'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent className="sm:max-w-lg">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>{t('hire.title', 'Contratar Profissional')}</ResponsiveModalTitle>
        </ResponsiveModalHeader>

        {/* Professional Info */}
        <div className="-mx-1 flex items-center gap-4 rounded-lg border border-border bg-muted/40 p-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-lg font-semibold text-primary-foreground">
            {professional.user.name
              ?.split(' ')
              .map((n) => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2) || 'P'}
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-foreground">{professional.user.name}</h3>
            <p className="text-sm text-primary">{professional.title}</p>
            <div className="mt-1 flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    size={12}
                    className={cn(i <= stars ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30')}
                  />
                ))}
              </div>
              <span className="text-xs text-muted-foreground">
                {t('hireModal.reviewCount', '({{count}} avaliações)', { count: professional.reviewCount })}
              </span>
            </div>
          </div>
          {professional.isAvailable && (
            <span className="flex items-center gap-1 rounded-full bg-green-500/10 px-2 py-1 text-xs text-green-600 dark:text-green-400">
              <CheckCircle size={12} />
              {t('hireModal.available', 'Disponível')}
            </span>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Link to Job (optional) */}
          {myJobs.length > 0 && (
            <div className="space-y-2">
              <Label>
                <Briefcase size={14} className="mr-1 inline" />
                {t('hire.linkToJob', 'Vincular à Vaga')} ({t('common.optional', 'opcional')})
              </Label>
              <Select
                value={selectedJob || 'none'}
                onValueChange={(v) => setSelectedJob(v === 'none' ? null : (v as string))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('hire.noJob', 'Contratação direta (sem vaga)')}</SelectItem>
                  {myJobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Agreed Rate */}
          <div className="space-y-2">
            <Label htmlFor="hire-rate">
              <DollarSign size={14} className="mr-1 inline" />
              {t('hire.agreedRate', 'Taxa Acordada')} (USD/hr)
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input
                id="hire-rate"
                type="number"
                step="0.01"
                min="0"
                value={customRate}
                onChange={(e) => setCustomRate(e.target.value)}
                placeholder={professional.hourlyRate ? String(professional.hourlyRate / 100) : '0.00'}
                className="pl-7"
              />
            </div>
            {professional.hourlyRate && (
              <p className="text-xs text-muted-foreground">
                {t('hire.suggestedRate', 'Taxa sugerida')}: ${(professional.hourlyRate / 100).toFixed(2)}/hr
              </p>
            )}
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="hire-message">
              {t('hire.message', 'Mensagem')} ({t('common.optional', 'opcional')})
            </Label>
            <Textarea
              id="hire-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('hire.messagePlaceholder', 'Apresente-se e descreva seu projeto...')}
              className="min-h-[100px]"
            />
          </div>

          <ResponsiveModalFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 size={16} className="animate-spin" />}
              {t('hire.sendOffer', 'Enviar Proposta')}
            </Button>
          </ResponsiveModalFooter>
        </form>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
